import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@economic/gaudi';
import { refreshCurrentLegacyPage } from '@economic/react-app';
import { useLocalization, t } from '../../../../../shared/components/l10n/l10n';
import useCachedQuery from '../../../../../shared/hooks/useCachedQuery';
import internalApiClient from '../../../../../shared/util/api/internalApiClient';
import { useEmailTemplates } from '../../invoices/utils/useEmailTemplates';
import { emailTemplateFormState } from '../../invoices/utils/emailTemplateFormState';
import { InvoiceLayoutType } from '../../../../../../types/internal-api-extra';
import {
    ReminderResendEmailOptionsRequest,
    ReminderResendEmailOptionsResponse,
    ReminderTypeEnum,
} from '../../../../../../types/internal-api';
import { ResendDialog } from '../../shared/components/ResendDialog';
import { RouteDisclosure } from '../../../../../shared/components/RouteDisclosure';
import { ResendFormData } from '../../shared/types';
import { SendByType } from '../../invoices/book-and-send/types';
import { splitEmails } from '../../invoices/utils';

export default function ResendReminderDialogContainer() {
    const navigate = useNavigate();
    const toast = useToast();
    const location = useLocation();
    const logNumber = (location.state as { logNumber: number }).logNumber;
    const textsLoading = useLocalization(textIds);

    const { loading: dataLoading, data, error: dataError } = useCachedQuery<ReminderResendEmailOptionsResponse>(
        `/sales/reminder/ResendEmailInformation?logId=${logNumber}`,
        null,
        { skip: !logNumber }
    );

    const { data: templates, loading: templatesLoading } = useEmailTemplates(
        data
            ? data.reminderType === ReminderTypeEnum.StatementOfAccount
                ? InvoiceLayoutType.AccountStatement
                : InvoiceLayoutType.Reminder
            : undefined
    );

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);

    const transformedData = useMemo<ResendFormData | undefined>(() => {
        if (!data) {
            return;
        }

        const {
            subject,
            body,
            recipients,
            recipientsCc,
            recipientsBcc,
            encrypt,
            includePdfLink,
            includePdfAsAttachment,
            ...rest
        } = data;

        return {
            ...rest,
            messages: [],
            sendByType: SendByType.email,
            emailOptions: {
                subject,
                body,
                recipients: recipients.join(' '),
                recipientsCc: recipientsCc.join(' '),
                recipientsBcc: recipientsBcc.join(' '),
                encrypt,
                includePdfLink,
                includePdfAsAttachment,
            },
            initialEmailTemplate: {
                subject: subject,
                body: body,
            },
            ...emailTemplateFormState(templates, false),
        };
    }, [data, templates]);

    const resend = async (values: ResendFormData) => {
        try {
            setLoading(true);

            const resendRequestData: ReminderResendEmailOptionsRequest = {
                ...values.emailOptions,
                logId: logNumber,
                recipients: splitEmails(values.emailOptions.recipients),
                recipientsCc: splitEmails(values.emailOptions.recipientsCc),
                recipientsBcc: splitEmails(values.emailOptions.recipientsBcc),
            };

            await internalApiClient.post(`/sales/reminder/ResendEmail`, resendRequestData);

            setLoading(false);
            toast.success(t`TOAST_REMINDER_SEND_AGAIN_SUCCESS`);

            refreshCurrentLegacyPage('content');
            hide();
        } catch (error) {
            if (error && error.response && error.response.status >= 500) {
                setError(true);
            }
        }
    };

    const hide = () => {
        navigate(location.pathname);
    };

    return (
        <ResendDialog
            title={t(`FORM_RESEND_REMINDER_TITLE`)}
            disclosure={<RouteDisclosure routeTrigger={'#resend-reminder'} />}
            information={transformedData}
            loading={dataLoading || templatesLoading || textsLoading || loading}
            onResend={resend}
            onHide={hide}
            error={error || !!dataError}
        />
    );
}

const textIds = [
    'TOAST_LOADING',
    'TOAST_REMINDER_SEND_AGAIN_SUCCESS',
    'FORM_BOOKANDSEND_SELECT_ATTACHMENT_ERROR',
    'FORM_BOOKANDSEND_SELECT_EMAIL_RECIPIENT_ERROR',
    'FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR',
    'FORM_RESEND_ERROR_TITLE',
    'FORM_RESEND_EMAIL',
    'SHARED_FORM_OK',
    'FORM_RESEND_BUTTON_CANCEL',
    'FORM_RESEND_BUTTON_SEND',
    'FORM_RESEND_REMINDER_TITLE',
];
